<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { GuidedShowcaseLayout, ActionBarLayout } from '@Shared';
import useFlowNavigation from '@Use/useFlowNavigation';
import { useCountdownTimer } from '@Use/useCountdownTimer';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useKbaStore } from '@Stores/kba.store';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import ShowcaseImage from '@Components/ShowcaseImage.vue';
import { trackClientKbaAnswersValidated } from '@Analytics/kba.mixpanel';

const KBA_TIMER_SECONDS = 180;
const TIMER_WARNING_THRESHOLD = 10;

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const kbaStore = useKbaStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const { advanceFlow } = useFlowNavigation();
const router = useRouter();
const route = useRoute();
const context = (route.query.context as string) ?? '';

const isSubmitting = ref(false);
const showRetryBanner = ref(false);
const showTimerExpiredBanner = ref(false);
const showLocalTimerBanner = ref(false);

const { timeLeft, start: startTimer, stop: stopTimer } = useCountdownTimer(KBA_TIMER_SECONDS);

const isTimerWarning = computed(() => timeLeft.value <= TIMER_WARNING_THRESHOLD && timeLeft.value > 0);
const canSubmit = computed(() => kbaStore.allAnswered && !isSubmitting.value);

const formattedTime = computed(() => {
    const minutes = Math.floor(timeLeft.value / 60);
    const seconds = timeLeft.value % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

const selectAnswer = (questionId: string, answerId: string) => {
    kbaStore.selectAnswer(questionId, answerId);
};

const handleNoMoreChances = async () => {
    stopTimer();
    await advanceFlow();
};

const submit = async () => {
    if (!canSubmit.value) return;

    isSubmitting.value = true;
    stopTimer();

    try {
        const response = await kbaStore.submitAnswers();

        if (!response) {
            router.push({ name: 'kba-error' });
            return;
        }

        trackClientKbaAnswersValidated(context, response.isPassed);

        if (response.isPassed) {
            await advanceFlow();
            return;
        }

        if (response.timeLimitReached) {
            if (response.canRetry) {
                showLocalTimerBanner.value = false;
                showTimerExpiredBanner.value = true;
                startTimer();
            } else {
                await handleNoMoreChances();
            }
            return;
        }

        if (response.questions && response.canRetry) {
            showLocalTimerBanner.value = false;
            showTimerExpiredBanner.value = false;
            showRetryBanner.value = true;
            startTimer();
            return;
        }

        // Locked out, failed all attempts, or invalid payload
        await handleNoMoreChances();
    } finally {
        isSubmitting.value = false;
    }
};

// Watch for timer expiring at 0
watch(timeLeft, (val) => {
    if (val === 0 && !isSubmitting.value) {
        // Timer hit zero locally — server will confirm on next submit
        showTimerExpiredBanner.value = false;
        showRetryBanner.value = false;
        showLocalTimerBanner.value = true;
    }
});

onMounted(async () => {
    // Guard: if no questions loaded or already locked out
    if (kbaStore.questions.length === 0 || kbaStore.noMoreChances) {
        isSubmitting.value = true;
        await advanceFlow();
        return;
    }

    showRetryBanner.value = false;
    showTimerExpiredBanner.value = false;
    showLocalTimerBanner.value = false;
    startTimer();
});
</script>

<template>
    <guided-showcase-layout
        suppress-back-to-dashboard
        title="Answer security questions"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">

        <app-brand-loader v-if="isSubmitting" class="loader">
            Verifying your answers...
            <template #caption>This may take a few seconds, please do not refresh this page</template>
        </app-brand-loader>

        <app-form-content-layout v-if="!isSubmitting">
            <p>Please answer the questions below. You will have 3 minutes to submit your answers.</p>

            <div class="kba-timer" :class="{ 'kba-timer--warning': isTimerWarning }">
                {{ formattedTime }}
            </div>

            <app-highlight-banner v-if="showLocalTimerBanner && !showTimerExpiredBanner" class="kba-banner">
                <p>Time's up — submit your answers to get new questions.</p>
            </app-highlight-banner>

            <app-highlight-banner v-if="showTimerExpiredBanner" class="kba-banner">
                <p>We've generated a new set of questions for you.</p>
            </app-highlight-banner>

            <app-highlight-banner v-if="showRetryBanner && !showTimerExpiredBanner" class="kba-banner">
                <p>Some answers were incorrect. Please try again with a new set of questions. Attempts remaining: {{ kbaStore.attemptsLeft }}</p>
            </app-highlight-banner>

            <div class="kba-questions-card">
                <div
                    v-for="(question, index) in kbaStore.questions"
                    :key="question.id"
                    class="kba-question">
                    <p class="kba-question__label">Question {{ index + 1 }} of {{ kbaStore.questions.length }}</p>
                    <p class="kba-question__text" data-hj-suppress>{{ question.text }}</p>
                    <div class="kba-question__answers" role="radiogroup">
                        <button
                            v-for="answer in question.potentialAnswers"
                            :key="answer.id"
                            type="button"
                            role="radio"
                            :aria-checked="kbaStore.selectedAnswers[question.id] === answer.id"
                            class="kba-answer"
                            :class="{ 'kba-answer--selected': kbaStore.selectedAnswers[question.id] === answer.id }"
                            @click="selectAnswer(question.id, answer.id)"
                            :data-id="`kba-answer-${answer.id}`">
                            <span data-hj-suppress>{{ answer.text }}</span>
                            <app-icon
                                v-if="kbaStore.selectedAnswers[question.id] === answer.id"
                                icon="cd-ic-check-square"
                                class="kba-answer__check" />
                        </button>
                    </div>
                </div>
            </div>

        </app-form-content-layout>

        <action-bar-layout v-if="!isSubmitting">
            <app-button
                size="large"
                @onClick="submit"
                :disabled="!canSubmit"
                :is-loading="isSubmitting"
                data-id="kba-submit-answers">
                Continue
            </app-button>
        </action-bar-layout>

        <template #branded>
            <showcase-image />
        </template>
    </guided-showcase-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.loader {
    margin-top: 5rem;
}

.kba-timer {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 700;
    color: $theme-primary;
    border: 3px solid $theme-primary;
    border-radius: 6px;
    padding: 0.25rem 1rem;
    width: 100%;

    &--warning {
        color: $red;
        border-color: $red;
    }
}

.kba-banner {
    margin-bottom: 1rem;
}

.kba-questions-card {
    background: #f7f7f7;
    border-radius: 6px;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 1rem;
}

.kba-question {
    &__label {
        font-size: 0.9rem;
        color: $black-med;
        margin-bottom: 0.5rem;
    }

    &__text {
        font-size: 1rem;
        font-weight: 700;
        color: $black-high;
        margin-bottom: 1.25rem;
    }

    &__answers {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }
}

.kba-answer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.625rem 1rem;
    height: 3rem;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: white;
    color: $black-med;
    font-size: 1rem;
    font-weight: 700;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &--selected {
        background: $theme-primary;
        border-color: $theme-primary;
        color: white;
    }

    &:hover:not(.kba-answer--selected) {
        border-color: $theme-primary;
    }

    &__check {
        flex-shrink: 0;
        font-size: 1.5rem;
    }
}
</style>
